<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-cog-outline me-2"></i>{{ $t('page.settings', 'Settings') }}</h4>
        </div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: tab==='profile' }" @click="tab='profile'">
          <i class="mdi mdi-hospital-building me-1"></i>Hospital Profile
        </button>
        <button class="hms-tab" :class="{ active: tab==='ai' }" @click="tab='ai'">
          <i class="mdi mdi-brain me-1"></i>AI & Integrations
        </button>
        <button class="hms-tab" :class="{ active: tab==='theme' }" @click="tab='theme'">
          <i class="mdi mdi-palette-outline me-1"></i>Branding & Theme
        </button>
        <button class="hms-tab" :class="{ active: tab==='sha' }" @click="tab='sha'">
          <i class="mdi mdi-shield-key-outline me-1"></i>SHA Integration
        </button>
        <button class="hms-tab" :class="{ active: tab==='mpesa' }" @click="tab='mpesa'; loadPaybills()">
          <i class="mdi mdi-cellphone me-1"></i>M-Pesa Paybills
        </button>
        <button class="hms-tab" :class="{ active: tab==='notifications' }" @click="tab='notifications'; loadNotificationSettings()">
          <i class="mdi mdi-bell-cog-outline me-1"></i>Notifications
        </button>
        <button class="hms-tab" :class="{ active: tab==='advanced' }" @click="tab='advanced'">
          <i class="mdi mdi-code-tags me-1"></i>Advanced Config
        </button>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <!-- ============ HOSPITAL PROFILE ============ -->
      <div v-else-if="tab === 'profile'" class="hms-card">
        <div class="hms-section-label">IDENTIFICATION</div>
        <div class="row g-3 mb-3">
          <div class="col-md-7"><label class="hms-label">Hospital Name *</label>
            <input class="hms-input" v-model="hosp.hospital_name" />
          </div>
          <div class="col-md-5"><label class="hms-label">Hospital Code</label>
            <input class="hms-input" v-model="hosp.hospital_code" disabled />
            <div class="hms-muted-sm mt-1">Code is permanent and cannot be changed.</div>
          </div>
          <div class="col-md-6"><label class="hms-label">Subscription Plan</label>
            <select class="hms-input" v-model="hosp.subscription_plan">
              <option value="basic">Basic</option>
              <option value="standard">Standard</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div class="col-md-6"><label class="hms-label">Logo URL</label>
            <input class="hms-input" v-model="hosp.logo_url" placeholder="https://..." />
          </div>
        </div>

        <div class="hms-section-label">CONTACT</div>
        <div class="row g-3 mb-3">
          <div class="col-md-6"><label class="hms-label">Email</label>
            <input class="hms-input" type="email" v-model="hosp.email" />
          </div>
          <div class="col-md-6"><label class="hms-label">Phone</label>
            <input class="hms-input" v-model="hosp.phone" />
          </div>
          <div class="col-md-6"><label class="hms-label">Country</label>
            <input class="hms-input" v-model="hosp.country" />
          </div>
          <div class="col-md-6"><label class="hms-label">City</label>
            <input class="hms-input" v-model="hosp.city" />
          </div>
          <div class="col-12"><label class="hms-label">Address</label>
            <input class="hms-input" v-model="hosp.address" />
          </div>
        </div>

        <div class="hms-section-label">REFERRAL NETWORK</div>
        <div class="row g-3 mb-3">
          <div class="col-md-6">
            <label class="hms-toggle">
              <input type="checkbox" :checked="hosp.is_referral_network == 1" @change="hosp.is_referral_network = $event.target.checked ? 1 : 0" />
              <span>This hospital operates a referral network with satellite branches</span>
            </label>
          </div>
          <div class="col-12" v-if="hosp.is_referral_network == 1"><label class="hms-label">Network Description</label>
            <textarea class="hms-input" rows="2" v-model="hosp.network_description" placeholder="e.g. Narok Referral Hospital with 5 Level 4 satellites"></textarea>
          </div>
        </div>

        <button class="hms-btn hms-btn-primary" @click="saveHospital" :disabled="processing">
          <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
          {{ processing ? 'Saving...' : 'Save Hospital Profile' }}
        </button>
      </div>

      <!-- ============ AI & INTEGRATIONS ============ -->
      <div v-else-if="tab === 'ai'" class="hms-card">
        <div class="hms-section-label">ANTHROPIC (CLAUDE) API KEY</div>
        <div class="hms-info-banner mb-3">
          <i class="mdi mdi-shield-key me-1"></i>
          The key powers AI Assistant features (clinical assist, ICD suggestions, radiology drafting).
          It is stored masked — last 4 characters are shown after saving. Test the connection after saving.
        </div>
        <div class="row g-3 mb-3">
          <div class="col-12"><label class="hms-label">Current Key</label>
            <div class="hms-input-row">
              <input class="hms-input" :type="showKey ? 'text' : 'password'" v-model="anthropicKey"
                     :placeholder="anthropicMasked || 'sk-ant-...'" />
              <button class="hms-btn hms-btn-blue" @click="showKey = !showKey" type="button" title="Show/hide">
                <i class="mdi" :class="showKey ? 'mdi-eye-off' : 'mdi-eye'"></i>
              </button>
            </div>
            <div class="hms-muted-sm mt-1" v-if="anthropicMasked && !anthropicKey">
              A key is currently saved: <span class="hms-mono">{{ anthropicMasked }}</span>. Type a new value above to overwrite.
            </div>
          </div>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-primary" @click="saveAnthropicKey" :disabled="processing || !anthropicKey">
            <i class="mdi mdi-content-save me-1"></i>Save Key
          </button>
          <button class="hms-btn hms-btn-blue" @click="testAnthropic" :disabled="processing || !anthropicMasked" v-if="anthropicMasked">
            <i class="mdi mdi-connection me-1"></i>Test Connection
          </button>
          <button class="hms-btn hms-btn-danger ms-auto" @click="clearAnthropicKey" v-if="anthropicMasked" :disabled="processing">
            <i class="mdi mdi-trash-can-outline me-1"></i>Remove Key
          </button>
        </div>

        <div v-if="testResult" class="mt-3">
          <div class="hms-alert" :class="testResult.success ? 'hms-alert-success' : 'hms-alert-danger'">
            <i class="mdi me-1" :class="testResult.success ? 'mdi-check-circle' : 'mdi-alert-circle'"></i>
            {{ testResult.message }}
          </div>
        </div>
      </div>

      <!-- ============ BRANDING & THEME ============ -->
      <div v-else-if="tab === 'theme'" class="hms-card">
        <div class="hms-section-label">DEFAULT APPEARANCE</div>
        <div class="row g-3 mb-3">
          <div class="col-md-6"><label class="hms-label">Default Theme</label>
            <select class="hms-input" v-model="theme.default_theme">
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
            <div class="hms-muted-sm mt-1">Applied to users on first login. Each user can override locally.</div>
          </div>
          <div class="col-md-6"><label class="hms-label">Accent Color (hex)</label>
            <div class="hms-input-row">
              <input class="hms-input" v-model="theme.accent_color" placeholder="#1a7fe8" />
              <span class="hms-color-swatch" :style="{ background: theme.accent_color || '#1a7fe8' }"></span>
            </div>
          </div>
        </div>

        <div class="hms-section-label">PREVIEW</div>
        <div class="hms-theme-preview" :data-theme="theme.default_theme">
          <div class="hms-preview-card">
            <div class="hms-preview-title" :style="{ color: theme.accent_color || '#1a7fe8' }">
              <i class="mdi mdi-hospital-building me-2"></i>{{ hosp.hospital_name || 'Your Hospital' }}
            </div>
            <button class="hms-preview-btn" :style="{ background: theme.accent_color || '#1a7fe8' }">Primary Action</button>
          </div>
        </div>

        <button class="hms-btn hms-btn-primary mt-3" @click="saveTheme" :disabled="processing">
          <i class="mdi mdi-content-save me-1"></i>Save Theme
        </button>
      </div>

      <!-- ============ SHA INTEGRATION ============ -->
      <div v-else-if="tab === 'sha'" class="hms-card">
        <div class="hms-info-banner mb-3">
          <i class="mdi mdi-information-outline me-1"></i>
          Configure SHA (Social Health Authority) integration. Get a JWT and Facility ID from
          <a href="https://developer.dha.go.ke" target="_blank">developer.dha.go.ke</a>, paste them here, then click <strong>Test Connection</strong>.
          <span v-if="sha.environment === 'uat'" class="d-block hms-muted-sm mt-1">Currently in <strong>UAT</strong> — submissions are sandbox only. Switch to PROD when you have production credentials.</span>
        </div>

        <div class="hms-section-label">ENVIRONMENT</div>
        <div class="row g-3 mb-3">
          <div class="col-md-4">
            <label class="hms-label">Environment *</label>
            <select class="hms-input" v-model="sha.environment" @change="onEnvChange">
              <option value="uat">UAT (sandbox)</option>
              <option value="prod">Production</option>
            </select>
          </div>
          <div class="col-md-8">
            <label class="hms-label">SHA Mediator Base URL *</label>
            <input class="hms-input" v-model="sha.base_url" placeholder="https://uat.dha.go.ke" />
            <div class="hms-muted-sm mt-1">UAT: <code>https://uat.dha.go.ke</code> · Prod: <code>https://api-edi.provider.sha.go.ke</code></div>
          </div>
          <div class="col-12">
            <label class="hms-label">FHIR Terminology Server URL</label>
            <input class="hms-input" v-model="sha.terminology_url" placeholder="https://qa-mis.apeiro-digital.com" />
            <div class="hms-muted-sm mt-1">Used in FHIR Bundle resource references. UAT: <code>https://qa-mis.apeiro-digital.com</code> · Prod: <code>https://fhir.sha.go.ke</code></div>
          </div>
        </div>

        <div class="hms-section-label">CREDENTIALS</div>
        <div class="row g-3 mb-3">
          <div class="col-12">
            <label class="hms-label">JWT Bearer Token *</label>
            <div class="hms-input-row">
              <input class="hms-input" :type="showJwt ? 'text' : 'password'" v-model="sha.jwt_token" :placeholder="sha.jwt_masked || 'Paste JWT from developer.dha.go.ke'" />
              <button class="hms-btn hms-btn-blue" type="button" @click="showJwt = !showJwt"><i class="mdi" :class="showJwt ? 'mdi-eye-off' : 'mdi-eye'"></i></button>
            </div>
            <div class="hms-muted-sm mt-1" v-if="sha.jwt_masked && !sha.jwt_token">
              A token is currently saved: <span class="hms-mono">{{ sha.jwt_masked }}</span>. Type a new one to overwrite.
            </div>
          </div>
          <div class="col-md-6">
            <label class="hms-label">Facility ID (FID) *</label>
            <div class="hms-input-row">
              <input class="hms-input" v-model="sha.facility_fid" placeholder="FID-33-100001-1" />
              <button class="hms-btn hms-btn-blue" type="button" @click="testFacility" :disabled="testingFid || !sha.facility_fid"><i class="mdi mdi-shield-check-outline me-1"></i>{{ testingFid ? 'Testing...' : 'Verify' }}</button>
            </div>
            <div class="hms-muted-sm mt-1" v-if="facilityResult" :class="facilityResult.success ? 'text-success' : 'text-danger'">
              <i class="mdi" :class="facilityResult.success ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline'"></i>
              {{ facilityResult.message }}
            </div>
          </div>
          <div class="col-md-6">
            <label class="hms-label">Default Practitioner PUID</label>
            <div class="hms-input-row">
              <input class="hms-input" v-model="sha.practitioner_puid" placeholder="PUID-0000002-1" />
              <button class="hms-btn hms-btn-blue" type="button" @click="testPractitioner" :disabled="testingPuid || !sha.practitioner_puid"><i class="mdi mdi-doctor me-1"></i>{{ testingPuid ? 'Testing...' : 'Verify' }}</button>
            </div>
            <div class="hms-muted-sm mt-1" v-if="practitionerResult" :class="practitionerResult.success ? 'text-success' : 'text-danger'">
              <i class="mdi" :class="practitionerResult.success ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline'"></i>
              {{ practitionerResult.message }}
            </div>
            <div class="hms-muted-sm mt-1">Each doctor's individual PUID is set on their staff record (Staff & HR).</div>
          </div>
        </div>

        <div class="hms-section-label">INBOUND WEBHOOK (SHA → HMS)</div>
        <div class="hms-info-banner mb-3" style="background: rgba(245,158,11,.08); color:#ca8a04; border-color: rgba(245,158,11,.3);">
          <i class="mdi mdi-information-outline me-1"></i>
          SHA pushes claim status updates to your callback URL. Share the URL + basic-auth credentials below with the SHA technical team.
        </div>
        <div class="row g-3 mb-3">
          <div class="col-12">
            <label class="hms-label">Webhook URL (read-only — share this with SHA)</label>
            <div class="hms-input-row">
              <input class="hms-input hms-mono" :value="webhookUrl" readonly />
              <button class="hms-btn hms-btn-blue" type="button" @click="copyWebhook"><i class="mdi" :class="copiedWebhook ? 'mdi-check' : 'mdi-content-copy'"></i> {{ copiedWebhook ? 'Copied' : 'Copy' }}</button>
            </div>
          </div>
          <div class="col-md-6">
            <label class="hms-label">Callback Basic-Auth Username</label>
            <input class="hms-input" v-model="sha.callback_user" placeholder="sha_callback_user" />
          </div>
          <div class="col-md-6">
            <label class="hms-label">Callback Basic-Auth Password</label>
            <div class="hms-input-row">
              <input class="hms-input" :type="showCbPass ? 'text' : 'password'" v-model="sha.callback_pass" :placeholder="sha.callback_pass_masked || 'Random secure password'" />
              <button class="hms-btn hms-btn-blue" type="button" @click="showCbPass = !showCbPass"><i class="mdi" :class="showCbPass ? 'mdi-eye-off' : 'mdi-eye'"></i></button>
              <button class="hms-btn hms-btn-amber" type="button" @click="generateCallbackPass" title="Generate"><i class="mdi mdi-dice-multiple"></i></button>
            </div>
          </div>
        </div>

        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-primary" @click="saveSha" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            <i class="mdi mdi-content-save me-1"></i>Save SHA Settings
          </button>
          <button class="hms-btn hms-btn-blue" @click="$router.push('/sha')">
            <i class="mdi mdi-shield-key-outline me-1"></i>Open SHA Console
          </button>
        </div>

        <div class="hms-section-label mt-4">QUICK CHECKLIST</div>
        <ul class="hms-check-list">
          <li :class="sha.jwt_masked || sha.jwt_token ? 'ok' : ''"><i class="mdi" :class="sha.jwt_masked || sha.jwt_token ? 'mdi-check' : 'mdi-circle-medium'"></i>JWT token saved</li>
          <li :class="sha.facility_fid ? 'ok' : ''"><i class="mdi" :class="sha.facility_fid ? 'mdi-check' : 'mdi-circle-medium'"></i>Facility FID set</li>
          <li :class="facilityResult && facilityResult.success ? 'ok' : ''"><i class="mdi" :class="facilityResult && facilityResult.success ? 'mdi-check' : 'mdi-circle-medium'"></i>Facility verified</li>
          <li :class="sha.practitioner_puid ? 'ok' : ''"><i class="mdi" :class="sha.practitioner_puid ? 'mdi-check' : 'mdi-circle-medium'"></i>Default practitioner PUID set</li>
          <li :class="sha.callback_user && (sha.callback_pass || sha.callback_pass_masked) ? 'ok' : ''"><i class="mdi" :class="sha.callback_user && (sha.callback_pass || sha.callback_pass_masked) ? 'mdi-check' : 'mdi-circle-medium'"></i>Callback credentials configured</li>
        </ul>
      </div>

      <!-- ============ M-PESA PAYBILLS ============ -->
      <div v-else-if="tab === 'mpesa'" class="hms-card">
        <div class="hms-info-banner mb-3">
          <i class="mdi mdi-information-outline me-1"></i>
          Each hospital sets its own Safaricom Daraja credentials. Multiple hospitals can share one paybill — each must use a unique <strong>Account Prefix</strong> (e.g. <code>NCRH</code>, <code>KIL</code>) so inbound payments route correctly.
        </div>

        <!-- Setup guide -->
        <div class="mb-3">
          <button class="hms-btn hms-btn-secondary" @click="showGuide = !showGuide">
            <i class="mdi" :class="showGuide ? 'mdi-chevron-up' : 'mdi-chevron-down'"></i>
            {{ showGuide ? 'Hide' : 'Show' }} setup guide — how to get your Daraja credentials
          </button>
          <div v-if="showGuide" class="mp-guide">
            <ol>
              <li><strong>Create a Daraja account & app.</strong> Visit <a href="https://developer.safaricom.co.ke" target="_blank">developer.safaricom.co.ke</a> → My Apps → Add a New App. Tick: Lipa Na M-Pesa, C2B, Transaction Status, Reversal, Account Balance.</li>
              <li><strong>Get Consumer Key & Consumer Secret</strong> from your app page.</li>
              <li><strong>Get your Shortcode</strong> — that's your Paybill or Till number from Safaricom (e.g. 4160303).</li>
              <li><strong>Get your Passkey</strong> — Safaricom sends this when you Go Live for Lipa Na M-Pesa Online. <em>It's not the consumer secret.</em></li>
              <li><strong>(Optional) Initiator Name + Security Credential</strong> — only needed for advanced APIs (reversal, txn status, balance). On Daraja → APIs → Security Credentials → Generate.</li>
            </ol>
            <p class="hms-muted-sm">For testing, set Environment to <strong>Sandbox</strong>. Production uses real money.</p>
          </div>
        </div>

        <button class="hms-btn hms-btn-primary mb-3" @click="openPaybillForm()">
          <i class="mdi mdi-plus"></i> Add Paybill
        </button>

        <!-- Form -->
        <div v-if="pbModal" class="mp-form">
          <h6>{{ pbForm.id ? 'Edit paybill' : 'New paybill' }}</h6>
          <div class="row g-2">
            <div class="col-md-3"><label class="hms-label">Shortcode *</label><input class="hms-input" v-model="pbForm.shortcode" placeholder="e.g. 4160303" /></div>
            <div class="col-md-3"><label class="hms-label">Account Prefix</label><input class="hms-input" v-model="pbForm.account_prefix" placeholder="e.g. NCRH" style="text-transform:uppercase" /><div class="hms-muted-sm">Required if shortcode is shared</div></div>
            <div class="col-md-3"><label class="hms-label">Label</label><input class="hms-input" v-model="pbForm.label" placeholder="e.g. Narok General OPD" /></div>
            <div class="col-md-3"><label class="hms-label">Type</label>
              <select class="hms-input" v-model="pbForm.type">
                <option value="paybill">Paybill</option>
                <option value="till">Till (Buy Goods)</option>
              </select>
            </div>
            <div class="col-md-3" v-if="pbForm.type === 'till'"><label class="hms-label">Till number</label><input class="hms-input" v-model="pbForm.till_number" placeholder="PartyB" /></div>
            <div class="col-md-3"><label class="hms-label">Purpose</label>
              <select class="hms-input" v-model="pbForm.purpose">
                <option value="all">All payments</option>
                <option value="outpatient">Outpatient</option>
                <option value="inpatient">Inpatient</option>
                <option value="pharmacy">Pharmacy</option>
                <option value="lab">Laboratory</option>
                <option value="radiology">Radiology</option>
                <option value="sha_copay">SHA co-pay</option>
                <option value="maternity">Maternity</option>
                <option value="donation">Donation</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="col-md-3"><label class="hms-label">Environment</label>
              <select class="hms-input" v-model="pbForm.environment">
                <option value="sandbox">Sandbox (testing)</option>
                <option value="production">Production (real money)</option>
              </select>
            </div>
            <div class="col-md-12"><label class="hms-label">Consumer Key *</label><input class="hms-input" v-model="pbForm.consumer_key" placeholder="From Daraja → Your App" /></div>
            <div class="col-md-12"><label class="hms-label">Consumer Secret *</label><input class="hms-input" v-model="pbForm.consumer_secret" placeholder="From Daraja → Your App" /></div>
            <div class="col-md-12"><label class="hms-label">Passkey</label><input class="hms-input" v-model="pbForm.passkey" placeholder="Lipa Na M-Pesa Online passkey" /></div>
            <div class="col-md-6"><label class="hms-label">Initiator Name <span class="hms-muted-sm">(optional)</span></label><input class="hms-input" v-model="pbForm.initiator_name" placeholder="API operator username" /></div>
            <div class="col-md-6"><label class="hms-label">Initiator Password <span class="hms-muted-sm">(optional)</span></label><input class="hms-input" v-model="pbForm.initiator_password" placeholder="Plain password used to generate the credential" /></div>
            <div class="col-md-12"><label class="hms-label">Security Credential <span class="hms-muted-sm">(encrypted from Daraja)</span></label>
              <textarea class="hms-input" v-model="pbForm.security_credential" rows="2" placeholder="Paste the encrypted blob from Daraja → APIs → Security Credentials → Generate" style="font-family:monospace;font-size:12px"></textarea>
            </div>
            <div class="col-md-12">
              <label class="hms-check"><input type="checkbox" v-model="pbForm.is_primary" :true-value="1" :false-value="0" /> Set as Primary (used for STK push)</label>
            </div>
          </div>
          <div class="d-flex gap-2 mt-3">
            <button class="hms-btn hms-btn-success" :disabled="savingPb" @click="savePaybill">
              <i class="mdi" :class="savingPb ? 'mdi-loading mdi-spin' : 'mdi-content-save'"></i> {{ pbForm.id ? 'Update' : 'Add paybill' }}
            </button>
            <button class="hms-btn hms-btn-secondary" @click="pbModal = false">Cancel</button>
          </div>
        </div>

        <!-- Paybill cards -->
        <div v-if="!paybills.length && !pbModal" class="text-center hms-pad hms-muted">
          <i class="mdi mdi-cellphone-off" style="font-size:48px"></i>
          <p>No paybills configured yet.</p>
        </div>
        <div v-for="pb in paybills" :key="pb.id" class="mp-card" :class="pb.is_primary == 1 ? 'mp-card-primary' : ''">
          <div class="mp-card-head">
            <div>
              <span class="mp-shortcode">{{ pb.shortcode }}</span>
              <span class="mp-pill" :class="pb.type === 'till' ? 'mp-pill-warn' : 'mp-pill-info'">{{ pb.type.toUpperCase() }}</span>
              <span v-if="pb.is_primary == 1" class="mp-pill mp-pill-success">⭐ PRIMARY</span>
              <span v-if="pb.callbacks_registered == 1" class="mp-pill mp-pill-success">✓ Active</span>
              <span v-if="pb.is_active != 1" class="mp-pill mp-pill-bad">Disabled</span>
              <span v-if="pb.account_prefix" class="mp-pill mp-pill-warn">prefix: {{ pb.account_prefix }}</span>
            </div>
            <div>
              <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="editPaybill(pb)" title="Edit"><i class="mdi mdi-pencil"></i></button>
              <button class="hms-btn hms-btn-secondary hms-btn-sm ms-1" @click="deletePaybill(pb)" title="Delete"><i class="mdi mdi-delete" style="color:#dc2626"></i></button>
            </div>
          </div>
          <div class="mp-card-meta">
            <span><strong>{{ pb.label }}</strong></span>
            <span class="hms-muted-sm">Purpose: {{ pb.purpose }}</span>
            <span class="hms-muted-sm">Env: {{ pb.environment }}</span>
            <span class="hms-muted-sm">Key: {{ pb.consumer_key ? pb.consumer_key.substring(0, 8) + '••••' : '—' }}</span>
            <span class="hms-muted-sm">Passkey: {{ pb.has_passkey == 1 ? '✓' : '—' }}</span>
            <span class="hms-muted-sm">Initiator: {{ pb.initiator_name ? '✓' : '—' }}</span>
          </div>
          <div class="mp-card-actions">
            <button class="hms-btn hms-btn-secondary hms-btn-sm" :disabled="pb._testing" @click="testConn(pb)">
              <i class="mdi" :class="pb._testing ? 'mdi-loading mdi-spin' : 'mdi-power-plug'"></i> Test connection
            </button>
            <button class="hms-btn hms-btn-success hms-btn-sm" :disabled="pb._registering" @click="registerCb(pb)">
              <i class="mdi" :class="pb._registering ? 'mdi-loading mdi-spin' : 'mdi-rocket-launch'"></i>
              {{ pb.callbacks_registered == 1 ? 'Re-register C2B' : 'Activate C2B callbacks' }}
            </button>
            <button class="hms-btn hms-btn-blue hms-btn-sm" :disabled="pb._stkTesting" @click="testStk(pb)">
              <i class="mdi" :class="pb._stkTesting ? 'mdi-loading mdi-spin' : 'mdi-cellphone-message'"></i> Test STK push
            </button>
            <button v-if="pb.is_primary != 1" class="hms-btn hms-btn-secondary hms-btn-sm" @click="setPrimary(pb)">⭐ Set as primary</button>
          </div>
          <div v-if="pb._msg" class="mp-msg" :class="pb._msgOk ? 'mp-msg-ok' : 'mp-msg-bad'">{{ pb._msg }}</div>
        </div>
      </div>

      <!-- ============ STK PUSH TEST MODAL ============ -->
      <div v-if="stkModal" class="mp-modal-bg" @click.self="closeStkModal">
        <div class="mp-modal">
          <div class="mp-modal-head">
            <div>
              <h5 class="m-0"><i class="mdi mdi-cellphone-message"></i> Test STK push</h5>
              <div class="hms-muted-sm">{{ stkForm.paybill_label }}</div>
            </div>
            <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="closeStkModal" :disabled="stkTesting"><i class="mdi mdi-close"></i></button>
          </div>
          <div class="mp-modal-body">
            <div v-if="!stkStatus">
              <div class="row g-2">
                <div class="col-md-6">
                  <label class="hms-label">Phone number</label>
                  <input class="hms-input" v-model="stkForm.phone" placeholder="07XX XXX XXX" autofocus />
                  <div class="hms-muted-sm">Any Kenyan format: 07…, 01…, 254…, +254…</div>
                </div>
                <div class="col-md-3">
                  <label class="hms-label">Amount (KES)</label>
                  <input class="hms-input" v-model.number="stkForm.amount" type="number" min="1" />
                </div>
                <div class="col-md-3">
                  <label class="hms-label">&nbsp;</label>
                  <div class="d-flex gap-1">
                    <button class="hms-btn hms-btn-secondary hms-btn-sm flex-fill" @click="stkForm.amount=1">1</button>
                    <button class="hms-btn hms-btn-secondary hms-btn-sm flex-fill" @click="stkForm.amount=10">10</button>
                    <button class="hms-btn hms-btn-secondary hms-btn-sm flex-fill" @click="stkForm.amount=100">100</button>
                  </div>
                </div>
                <div class="col-md-12">
                  <label class="hms-label">Description (what the user sees on phone)</label>
                  <input class="hms-input" v-model="stkForm.description" maxlength="40" />
                </div>
              </div>
            </div>

            <div v-if="stkStatus" class="mp-stk-status" :class="stkOk === true ? 'mp-stk-ok' : (stkOk === false ? 'mp-stk-bad' : 'mp-stk-pending')">
              <div class="mp-stk-icon">
                <i v-if="stkOk === true" class="mdi mdi-check-circle"></i>
                <i v-else-if="stkOk === false" class="mdi mdi-close-circle"></i>
                <i v-else class="mdi mdi-loading mdi-spin"></i>
              </div>
              <div class="mp-stk-msg">{{ stkStatus }}</div>
            </div>

            <div v-if="stkStatus && stkCheckoutId" class="hms-muted-sm mt-2">
              CheckoutRequestID: <span class="hms-mono">{{ stkCheckoutId }}</span>
            </div>
          </div>
          <div class="mp-modal-foot">
            <button v-if="!stkTesting && stkOk !== true" class="hms-btn hms-btn-secondary" @click="closeStkModal">Close</button>
            <button v-if="stkOk === true || stkOk === false" class="hms-btn hms-btn-secondary" @click="resetStkForm">Send another</button>
            <button v-if="!stkStatus" class="hms-btn hms-btn-primary" :disabled="!canSendStk || stkTesting" @click="sendStkTest">
              <i class="mdi" :class="stkTesting ? 'mdi-loading mdi-spin' : 'mdi-send'"></i>
              {{ stkTesting ? 'Sending…' : 'Send STK push' }}
            </button>
          </div>
        </div>
      </div>

      <!-- ============ NOTIFICATIONS (SMS + EMAIL) ============ -->
      <div v-else-if="tab === 'notifications'">
        <div class="hms-card">
          <div class="hms-muted mb-3">
            <i class="mdi mdi-information-outline me-1"></i>
            Configure SMS (Africa's Talking) and Email (SMTP — Brevo / Sendinblue / any provider). Patient registrations and payment receipts auto-fire from here.
          </div>

          <!-- ===== SMS section ===== -->
          <div class="hms-section-label">SMS (AFRICA'S TALKING)</div>
          <div class="row g-3 mb-3">
            <div class="col-md-4"><label class="hms-label">Username</label>
              <input class="hms-input" v-model="notif.sms_username" placeholder="e.g. SHULEH" />
            </div>
            <div class="col-md-5"><label class="hms-label">API Key</label>
              <input class="hms-input" v-model="notif.sms_api_key" placeholder="atsk_..." type="password" autocomplete="off" />
              <div class="hms-muted-sm mt-1">Leave dots in place to keep the saved key.</div>
            </div>
            <div class="col-md-3"><label class="hms-label">Sender ID</label>
              <input class="hms-input" v-model="notif.sms_sender_id" placeholder="e.g. SHULEH" maxlength="11" />
            </div>
            <div class="col-md-3"><label class="hms-check">
              <input type="checkbox" v-model="notif.sms_sandbox" :true-value="1" :false-value="0" /> Sandbox mode
            </label></div>
          </div>

          <!-- SMS test -->
          <div class="hms-bordered-block mb-4">
            <div class="hms-section-label">📲 SEND A TEST SMS</div>
            <div class="row g-2 align-items-end">
              <div class="col-md-5"><label class="hms-label">Phone</label>
                <input class="hms-input" v-model="smsTest.phone" placeholder="07XX XXX XXX" />
              </div>
              <div class="col-md-5"><label class="hms-label">Message (optional)</label>
                <input class="hms-input" v-model="smsTest.message" placeholder="Default test message will be sent" />
              </div>
              <div class="col-md-2">
                <button class="hms-btn hms-btn-primary w-100" :disabled="smsTest.busy" @click="doSendTestSms">
                  <i class="mdi" :class="smsTest.busy ? 'mdi-loading mdi-spin' : 'mdi-send'"></i>
                  {{ smsTest.busy ? 'Sending…' : 'Send' }}
                </button>
              </div>
            </div>
            <div v-if="smsTest.result" class="mt-2 hms-test-result" :class="smsTest.ok ? 'hms-test-ok' : 'hms-test-bad'">
              <i class="mdi" :class="smsTest.ok ? 'mdi-check-circle' : 'mdi-close-circle'"></i>
              <span>{{ smsTest.result }}</span>
            </div>
          </div>

          <!-- ===== Email section ===== -->
          <div class="hms-section-label">EMAIL (SMTP)</div>
          <div class="row g-3 mb-3">
            <div class="col-md-4"><label class="hms-label">Provider</label>
              <select class="hms-input" v-model="emailProvider" @change="onEmailProviderChange">
                <option value="brevo">Brevo (Sendinblue)</option>
                <option value="gmail">Gmail SMTP</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div class="col-md-3"><label class="hms-label">SMTP Host</label>
              <input class="hms-input" v-model="notif.smtp_host" :disabled="emailProvider !== 'custom'"
                     :placeholder="emailProvider === 'gmail' ? 'smtp.gmail.com' : 'smtp-relay.brevo.com'" />
            </div>
            <div class="col-md-2"><label class="hms-label">Port</label>
              <input type="number" class="hms-input" v-model.number="notif.smtp_port" :disabled="emailProvider !== 'custom'"
                     :placeholder="emailProvider === 'gmail' ? '587' : '2525'" />
            </div>
            <div class="col-md-3"><label class="hms-label">Encryption</label>
              <select class="hms-input" v-model="notif.smtp_secure">
                <option value="tls">TLS</option>
                <option value="ssl">SSL</option>
                <option value="none">None</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">SMTP Username</label>
              <input class="hms-input" v-model="notif.smtp_username" placeholder="user@smtp-brevo.com" />
            </div>
            <div class="col-md-6"><label class="hms-label">SMTP Password</label>
              <input class="hms-input" v-model="notif.smtp_password" placeholder="••••" type="password" autocomplete="off" />
              <div class="hms-muted-sm mt-1">Leave dots to keep saved password.</div>
            </div>
            <div class="col-md-6"><label class="hms-label">From Email *</label>
              <input class="hms-input" v-model="notif.smtp_from_email" placeholder="must be a verified Brevo sender (e.g. sstmgenerated@gmail.com)" />
              <div class="hms-muted-sm mt-1">Required. Brevo rejects empty From with "451 Invalid from".</div>
            </div>
            <div class="col-md-6"><label class="hms-label">From Name</label>
              <input class="hms-input" v-model="notif.smtp_from_name" placeholder="Narok Referral Hospital" />
            </div>
          </div>

          <!-- Email test -->
          <div class="hms-bordered-block mb-4">
            <div class="hms-section-label">✉ SEND A TEST EMAIL</div>
            <div class="row g-2 align-items-end">
              <div class="col-md-5"><label class="hms-label">Recipient email</label>
                <input class="hms-input" v-model="emailTest.email" placeholder="you@example.com" />
              </div>
              <div class="col-md-5"><label class="hms-label">Subject (optional)</label>
                <input class="hms-input" v-model="emailTest.subject" placeholder="Test email from HMS" />
              </div>
              <div class="col-md-2">
                <button class="hms-btn hms-btn-primary w-100" :disabled="emailTest.busy" @click="doSendTestEmail">
                  <i class="mdi" :class="emailTest.busy ? 'mdi-loading mdi-spin' : 'mdi-email-arrow-right-outline'"></i>
                  {{ emailTest.busy ? 'Sending…' : 'Send' }}
                </button>
              </div>
            </div>
            <div v-if="emailTest.result" class="mt-2 hms-test-result" :class="emailTest.ok ? 'hms-test-ok' : 'hms-test-bad'">
              <i class="mdi" :class="emailTest.ok ? 'mdi-check-circle' : 'mdi-close-circle'"></i>
              <span>{{ emailTest.result }}</span>
            </div>
          </div>

          <!-- Misc + save -->
          <div class="hms-section-label">DELIVERY POLICY</div>
          <div class="row g-3 mb-3">
            <div class="col-md-3"><label class="hms-label">Quiet hours — start</label>
              <input type="time" class="hms-input" v-model="notif.quiet_hours_start" />
            </div>
            <div class="col-md-3"><label class="hms-label">Quiet hours — end</label>
              <input type="time" class="hms-input" v-model="notif.quiet_hours_end" />
            </div>
            <div class="col-md-3"><label class="hms-label">Daily cap (0 = no cap)</label>
              <input type="number" class="hms-input" v-model.number="notif.daily_cap" min="0" />
            </div>
            <div class="col-md-3"><label class="hms-check mt-4">
              <input type="checkbox" v-model="notif.is_active" :true-value="1" :false-value="0" /> Notifications enabled
            </label></div>
          </div>

          <div class="d-flex gap-2 mt-3">
            <button class="hms-btn hms-btn-primary" :disabled="notifSaving" @click="saveNotifSettings">
              <i class="mdi" :class="notifSaving ? 'mdi-loading mdi-spin' : 'mdi-content-save'"></i>
              {{ notifSaving ? 'Saving…' : 'Save Notification Settings' }}
            </button>
            <button class="hms-btn hms-btn-secondary" @click="loadNotificationSettings">
              <i class="mdi mdi-reload"></i> Reload
            </button>
          </div>
        </div>
      </div>

      <!-- ============ ADVANCED CONFIG ============ -->
      <div v-else-if="tab === 'advanced'" class="hms-card">
        <div class="hms-info-banner mb-3">
          <i class="mdi mdi-alert-outline me-1"></i>
          Advanced — direct access to the <code>system_config</code> key/value store. Sensitive keys (containing
          <code>_key</code>, <code>_secret</code>, <code>_password</code>, <code>_token</code>) are masked on read.
        </div>

        <div class="d-flex gap-2 mb-3">
          <button class="hms-btn hms-btn-primary" @click="openConfigForm()">
            <i class="mdi mdi-plus me-1"></i>Add Setting
          </button>
        </div>

        <div v-if="configRows.length" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr>
              <th>Key</th><th>Value</th><th>Description</th><th>Updated</th><th>Actions</th>
            </tr></thead>
            <tbody>
              <tr v-for="c in configRows" :key="c.config_id">
                <td class="hms-mono">{{ c.config_key }} <span class="hms-badge hms-badge-warning ms-1" v-if="c.is_sensitive == 1">sensitive</span></td>
                <td><span class="hms-mono hms-text-clip">{{ c.config_value || '—' }}</span></td>
                <td>{{ c.description || '—' }}</td>
                <td><span class="hms-muted-sm">{{ formatDateTime(c.updated_at) }}</span></td>
                <td class="d-flex gap-1">
                  <button class="hms-btn hms-btn-sm hms-btn-amber" @click="openConfigForm(c)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                  <button class="hms-btn hms-btn-sm hms-btn-danger" @click="confirmDeleteConfig(c)" title="Delete"><i class="mdi mdi-trash-can"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-database-cog"></i>No advanced settings yet.</div>
      </div>

      <!-- Config form modal -->
      <div class="hms-modal-overlay" v-if="showConfigForm" @click.self="showConfigForm=false">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-database-cog"></i> {{ configForm.original_key ? 'Edit Setting' : 'Add Setting' }}</span>
            <button class="hms-modal-close" @click="showConfigForm=false">&times;</button>
          </div>
          <div v-if="configFormError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ configFormError }}</div>

          <div class="row g-3 mb-3">
            <div class="col-12"><label class="hms-label">Config Key *</label>
              <input class="hms-input" v-model="configForm.config_key" :disabled="!!configForm.original_key" placeholder="e.g. queue_announcement_voice" />
              <div class="hms-muted-sm mt-1" v-if="!configForm.original_key">Letters, digits, underscores only.</div>
            </div>
            <div class="col-12"><label class="hms-label">Value</label>
              <input class="hms-input" v-model="configForm.config_value" placeholder="value" />
              <div class="hms-muted-sm mt-1" v-if="configForm.is_sensitive == 1">
                <i class="mdi mdi-shield-key"></i> Sensitive — leave blank to keep current value.
              </div>
            </div>
            <div class="col-12"><label class="hms-label">Description</label>
              <textarea class="hms-input" rows="2" v-model="configForm.description"></textarea>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="saveConfig" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Saving...' : 'Save Setting' }}
          </button>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; }
.hms-text-clip { display: inline-block; max-width: 360px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle; }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 24px; margin-bottom: 16px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-input:disabled { background: var(--hms-bg3); color: var(--hms-m); }
.hms-input-row { display: flex; gap: 8px; align-items: center; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-top: 8px; margin-bottom: 8px; }

.hms-bordered-block { border: 1px dashed var(--hms-br); border-radius: 10px; padding: 14px; background: var(--hms-bg2); }
.hms-test-result { display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 8px; font-size: 13px; line-height: 1.4; }
.hms-test-result i { font-size: 18px; flex-shrink: 0; }
.hms-test-ok  { background: rgba(40,167,69,.1);  color: var(--hms-success); border: 1px solid rgba(40,167,69,.3); }
.hms-test-bad { background: rgba(220,53,69,.1); color: var(--hms-danger);  border: 1px solid rgba(220,53,69,.3); }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: top; }

.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; display: inline-block; }
.hms-badge-warning { background: rgba(255,193,7,.15); color: var(--hms-warning); }

.hms-toggle { display: flex; align-items: center; gap: 8px; cursor: pointer; color: var(--hms-h); font-size: 13px; }
.hms-toggle input { width: 18px; height: 18px; accent-color: var(--hms-primary); }

.hms-color-swatch { display: inline-block; width: 36px; height: 36px; border-radius: 8px; border: 1px solid var(--hms-input-br); flex-shrink: 0; }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-danger { background: rgba(220,53,69,.15); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-danger:hover:not(:disabled) { background: rgba(220,53,69,.25); }

.hms-info-banner { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.hms-info-banner code { background: rgba(0,0,0,0.08); padding: 1px 4px; border-radius: 4px; }
.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-alert-success { background: rgba(40,167,69,.1); color: var(--hms-success); border: 1px solid rgba(40,167,69,.3); }

.hms-theme-preview { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 12px; padding: 24px; }
.hms-preview-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 10px; padding: 18px; }
.hms-preview-title { font-size: 16px; font-weight: 700; margin-bottom: 12px; }
.hms-preview-btn { color: #fff; border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 720px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-sm { max-width: 460px; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }

/* SHA tab */
.hms-input-row code { background: var(--hms-bg3); padding: 1px 5px; border-radius: 3px; font-size: 11px; }
.hms-info-banner code { background: rgba(0,0,0,0.08); padding: 1px 4px; border-radius: 4px; font-size: 12px; }
.text-success { color: var(--hms-success); }
.text-danger { color: var(--hms-danger); }
.hms-check-list { list-style: none; padding: 0; margin: 0; }
.hms-check-list li { display: flex; align-items: center; gap: 8px; padding: 4px 0; font-size: 13px; color: var(--hms-s); }
.hms-check-list li.ok { color: var(--hms-success); }
.hms-check-list li i { font-size: 16px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }

/* ===== M-Pesa paybill ===== */
.mp-guide { background: var(--hms-bg1); border-left: 3px solid #4FC3F7; border-radius: 6px; padding: 14px 18px; margin-top: 8px; font-size: 13px; line-height: 1.7; }
.mp-guide ol { margin: 0; padding-left: 20px; }
.mp-guide a { color: #2b6cb0; }

.mp-form { background: var(--hms-bg1); border-radius: 10px; padding: 16px; margin-bottom: 16px; border-left: 3px solid var(--hms-primary); }
.mp-form h6 { margin: 0 0 12px; color: var(--hms-primary); }

.mp-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 10px; padding: 14px; margin-bottom: 12px; border-left: 3px solid var(--hms-br); }
.mp-card-primary { border-left-color: #22c55e; }
.mp-card-head { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
.mp-shortcode { font-size: 18px; font-weight: 700; margin-right: 8px; }
.mp-pill { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; margin: 0 2px; }
.mp-pill-info { background: rgba(79,195,247,.15); color: #4FC3F7; }
.mp-pill-success { background: rgba(34,197,94,.15); color: #22c55e; }
.mp-pill-warn { background: rgba(245,158,11,.15); color: #ea580c; }
.mp-pill-bad { background: rgba(239,68,68,.15); color: #ef4444; }
.mp-card-meta { display: flex; gap: 14px; flex-wrap: wrap; margin-bottom: 10px; font-size: 13px; }
.mp-card-actions { display: flex; gap: 6px; flex-wrap: wrap; }
.mp-msg { margin-top: 10px; padding: 8px 12px; border-radius: 6px; font-size: 12px; }
.mp-msg-ok { background: rgba(34,197,94,.1); color: #22c55e; }
.mp-msg-bad { background: rgba(239,68,68,.1); color: #ef4444; }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "SettingsManagement",
  components: { Navigation },
  data() {
    return {
      loading: true, processing: false,
      tab: "profile",

      hosp: { hospital_name: "", hospital_code: "", email: "", phone: "", address: "", country: "Kenya", city: "", logo_url: "", subscription_plan: "standard", is_referral_network: 0, network_description: "" },

      configRows: [],
      anthropicKey: "",
      anthropicMasked: "",
      showKey: false,
      testResult: null,

      theme: { default_theme: "light", accent_color: "#1a7fe8" },

      // SHA Integration tab
      sha: {
        environment: 'uat',
        base_url: 'https://uat.dha.go.ke',
        terminology_url: 'https://qa-mis.apeiro-digital.com',
        jwt_token: '', jwt_masked: '',
        facility_fid: '',
        practitioner_puid: '',
        callback_user: '',
        callback_pass: '', callback_pass_masked: ''
      },
      showJwt: false, showCbPass: false,
      testingFid: false, testingPuid: false,
      facilityResult: null, practitionerResult: null,
      copiedWebhook: false,

      showConfigForm: false,
      configForm: { original_key: "", config_key: "", config_value: "", description: "", is_sensitive: 0 },
      configFormError: "",

      // M-Pesa
      paybills: [],
      showGuide: false,
      pbModal: false, savingPb: false,
      pbForm: this.blankPaybillForm(),
      // STK test modal
      stkModal: false, stkTesting: false,
      stkForm: { paybill_id: null, paybill_label: '', phone: '', amount: 1, description: 'HMS test STK' },
      stkStatus: '', stkOk: null, stkCheckoutId: '',

      // Notification settings
      notif: this.blankNotif(),
      emailProvider: 'brevo',
      notifSaving: false,
      smsTest:   { phone: '', message: '', busy: false, result: '', ok: false },
      emailTest: { email: '', subject: '', busy: false, result: '', ok: false },

      toastMsg: "", toastError: false, toastTimer: null
    };
  },
  computed: {
    webhookUrl() {
      var base = (process.env.VUE_APP_BASE_URL_HMS || '') + '/index.php';
      var hospId = sessionStorage.getItem('hospitalId') || (this.hosp && this.hosp.hospital_id) || '11';
      return base + '?function=shaWebhook&hospital_id=' + hospId;
    },
    canSendStk() {
      var f = this.stkForm || {};
      var phone = (f.phone || '').replace(/\s+/g, '');
      // Accept 07XXXXXXXX, 01XXXXXXXX, 2547XXXXXXXX, 2541XXXXXXXX, or +2547/+2541…
      var phoneOk = /^(?:\+?254|0)(?:7|1)\d{8}$/.test(phone);
      var amountOk = Number(f.amount) >= 1;
      var descOk = !!(f.description && String(f.description).trim());
      return phoneOk && amountOk && descOk && !!f.paybill_id;
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.loadAll();
  },
  beforeUnmount() { if (this.toastTimer) clearTimeout(this.toastTimer); },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },
    formatDateTime(s) { if (!s) return "—"; var d = new Date(s.replace ? s.replace(" ","T") : s); return isNaN(d) ? s : d.toLocaleString(); },

    async loadAll() {
      this.loading = true;
      try {
        var res = await Promise.all([
          executeJson({ function: "getHospitalProfile" }, hms),
          executeJson({ function: "getSystemConfig" }, hms)
        ]);
        if (res[0].data && res[0].data.success) this.hosp = Object.assign(this.hosp, res[0].data.data.Hospital || {});
        if (res[1].data && res[1].data.success) {
          this.configRows = (res[1].data.data && res[1].data.data.Config) || [];
          this.applyConfigToForms();
        }
      } catch(e) { console.error(e); }
      this.loading = false;
    },
    applyConfigToForms() {
      var k = this.configRows.find(c => c.config_key === 'anthropic_api_key');
      this.anthropicMasked = (k && k.has_value == 1) ? k.config_value : "";

      var dt = this.configRows.find(c => c.config_key === 'default_theme');
      var ac = this.configRows.find(c => c.config_key === 'accent_color');
      this.theme.default_theme = (dt && dt.config_value) ? dt.config_value : "light";
      this.theme.accent_color  = (ac && ac.config_value) ? ac.config_value : "#1a7fe8";

      // SHA fields — pull from configRows
      var pick = (key) => {
        var row = this.configRows.find(c => c.config_key === key);
        return row ? { value: row.config_value, has: row.has_value == 1, sensitive: row.is_sensitive == 1 } : null;
      };
      var env = pick('sha_environment'); if (env) this.sha.environment       = env.value || 'uat';
      var bu  = pick('sha_base_url');    if (bu)  this.sha.base_url          = bu.value  || 'https://uat.dha.go.ke';
      var tu  = pick('sha_terminology_url'); if (tu) this.sha.terminology_url = tu.value || 'https://qa-mis.apeiro-digital.com';
      var fid = pick('sha_facility_fid');     if (fid) this.sha.facility_fid      = fid.value || '';
      var pp  = pick('sha_practitioner_puid'); if (pp) this.sha.practitioner_puid = pp.value || '';
      var cu  = pick('sha_callback_user');    if (cu) this.sha.callback_user     = cu.value || '';
      var jt  = pick('sha_jwt_token');         if (jt) this.sha.jwt_masked        = jt.has ? jt.value : '';
      var cp  = pick('sha_callback_pass');     if (cp) this.sha.callback_pass_masked = cp.has ? cp.value : '';
    },

    onEnvChange() {
      if (this.sha.environment === 'prod') {
        this.sha.base_url        = 'https://api-edi.provider.sha.go.ke';
        this.sha.terminology_url = 'https://fhir.sha.go.ke';
      } else {
        this.sha.base_url        = 'https://uat.dha.go.ke';
        this.sha.terminology_url = 'https://qa-mis.apeiro-digital.com';
      }
    },

    async saveSha() {
      this.processing = true;
      try {
        var pairs = [
          ['sha_environment',     this.sha.environment,     'SHA environment: uat or prod'],
          ['sha_base_url',        this.sha.base_url,        'SHA mediator base URL'],
          ['sha_terminology_url', this.sha.terminology_url, 'SHA FHIR terminology server URL'],
          ['sha_facility_fid',    this.sha.facility_fid,    'SHA Facility ID for this hospital'],
          ['sha_practitioner_puid', this.sha.practitioner_puid, 'Default practitioner PUID'],
          ['sha_callback_user',   this.sha.callback_user,   'Basic-auth username for SHA callback']
        ];
        // Only update token/password if user typed a new value (don't overwrite with masked placeholder)
        if (this.sha.jwt_token)     pairs.push(['sha_jwt_token',     this.sha.jwt_token,     'SHA JWT Bearer token']);
        if (this.sha.callback_pass) pairs.push(['sha_callback_pass', this.sha.callback_pass, 'Basic-auth password for SHA callback']);

        for (var i = 0; i < pairs.length; i++) {
          var p = pairs[i];
          await executeJson({ function: 'setSystemConfig', config_key: p[0], config_value: p[1], description: p[2] }, hms);
        }
        this.showToast("SHA settings saved");
        this.sha.jwt_token = ''; this.sha.callback_pass = '';
        await this.loadAll();
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    async testFacility() {
      this.testingFid = true; this.facilityResult = null;
      try {
        var r = await executeJson({ function: 'verifyShaFacility', fid: this.sha.facility_fid }, hms);
        this.facilityResult = { success: !!(r.data && r.data.success), message: (r.data && r.data.message) || 'Verified.' };
      } catch(e) { this.facilityResult = { success: false, message: 'Connection error' }; }
      this.testingFid = false;
    },
    async testPractitioner() {
      this.testingPuid = true; this.practitionerResult = null;
      try {
        var r = await executeJson({ function: 'verifyShaPractitioner', puid: this.sha.practitioner_puid }, hms);
        this.practitionerResult = { success: !!(r.data && r.data.success), message: (r.data && r.data.message) || 'Verified.' };
      } catch(e) { this.practitionerResult = { success: false, message: 'Connection error' }; }
      this.testingPuid = false;
    },

    generateCallbackPass() {
      var chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
      var s = '';
      for (var i = 0; i < 24; i++) s += chars.charAt(Math.floor(Math.random() * chars.length));
      this.sha.callback_pass = s;
      this.showCbPass = true;
    },
    copyWebhook() {
      var self = this;
      navigator.clipboard.writeText(this.webhookUrl).then(() => {
        self.copiedWebhook = true;
        setTimeout(() => self.copiedWebhook = false, 1500);
      });
    },

    async saveHospital() {
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "updateHospitalProfile" }, this.hosp), hms);
        if (r.data && r.data.success) this.showToast("Hospital profile saved");
        else this.showToast(r.data.message || "Failed", true);
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    async saveAnthropicKey() {
      if (!this.anthropicKey) return;
      this.processing = true; this.testResult = null;
      try {
        var r = await executeJson({
          function: "setSystemConfig",
          config_key: "anthropic_api_key",
          config_value: this.anthropicKey,
          description: "Anthropic Claude API key for AI features"
        }, hms);
        if (r.data && r.data.success) {
          this.anthropicKey = ""; this.showKey = false;
          this.showToast("Key saved");
          await this.loadAll();
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    async clearAnthropicKey() {
      if (!confirm("Remove the Anthropic API key? AI features will stop working until a new key is saved.")) return;
      this.processing = true;
      try {
        var r = await executeJson({ function: "deleteSystemConfig", config_key: "anthropic_api_key" }, hms);
        if (r.data && r.data.success) { this.showToast("Key removed"); await this.loadAll(); }
        else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    async testAnthropic() {
      this.processing = true; this.testResult = null;
      try {
        var r = await executeJson({ function: "testAnthropicKey" }, hms);
        this.testResult = { success: !!(r.data && r.data.success), message: (r.data && r.data.message) || "Unknown response" };
      } catch(e) { this.testResult = { success: false, message: "Connection error." }; }
      this.processing = false;
    },

    async saveTheme() {
      this.processing = true;
      try {
        var calls = [
          executeJson({ function: "setSystemConfig", config_key: "default_theme", config_value: this.theme.default_theme, description: "Default theme for new users" }, hms),
          executeJson({ function: "setSystemConfig", config_key: "accent_color",  config_value: this.theme.accent_color,  description: "Accent color (hex)" }, hms)
        ];
        await Promise.all(calls);
        this.showToast("Theme saved");
        await this.loadAll();
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    openConfigForm(c) {
      this.configFormError = "";
      if (c) {
        this.configForm = { original_key: c.config_key, config_key: c.config_key, config_value: "", description: c.description || "", is_sensitive: c.is_sensitive || 0 };
      } else {
        this.configForm = { original_key: "", config_key: "", config_value: "", description: "", is_sensitive: 0 };
      }
      this.showConfigForm = true;
    },
    async saveConfig() {
      this.configFormError = "";
      var f = this.configForm;
      if (!f.config_key) { this.configFormError = "Key is required."; return; }
      if (!f.original_key && !/^[a-z][a-z0-9_]*$/i.test(f.config_key)) { this.configFormError = "Key must be alphanumeric with underscores."; return; }
      // Sensitive edit: if value left blank, skip update
      if (f.original_key && f.is_sensitive == 1 && !f.config_value) {
        if (!confirm("Value left blank — current sensitive value will be kept. Update only the description?")) return;
      }

      this.processing = true;
      try {
        var payload = { function: "setSystemConfig", config_key: f.config_key, description: f.description };
        if (!(f.original_key && f.is_sensitive == 1 && !f.config_value)) {
          payload.config_value = f.config_value;
        }
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.showConfigForm = false; this.showToast("Setting saved");
          await this.loadAll();
        } else { this.configFormError = r.data.message || "Failed"; }
      } catch(e) { this.configFormError = "Connection error."; }
      this.processing = false;
    },
    async confirmDeleteConfig(c) {
      if (!confirm('Delete setting "' + c.config_key + '"?')) return;
      try {
        var r = await executeJson({ function: "deleteSystemConfig", config_key: c.config_key }, hms);
        if (r.data && r.data.success) { this.showToast("Setting removed"); await this.loadAll(); }
        else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
    },

    // ============ M-PESA PAYBILLS ============
    blankPaybillForm() {
      return { id: null, shortcode: '', till_number: '', account_prefix: '', label: 'M-Pesa',
        purpose: 'all', type: 'paybill', consumer_key: '', consumer_secret: '', passkey: '',
        environment: 'sandbox', initiator_name: '', initiator_password: '', security_credential: '',
        is_primary: 0 };
    },

    // ===== NOTIFICATIONS =====
    blankNotif() {
      return {
        sms_username: '', sms_api_key: '', sms_sender_id: '', sms_sandbox: 0,
        smtp_host: '', smtp_port: 2525, smtp_username: '', smtp_password: '',
        smtp_from_email: '', smtp_from_name: '', smtp_secure: 'tls',
        quiet_hours_start: '', quiet_hours_end: '', daily_cap: 0, is_active: 1
      };
    },
    inferEmailProvider(host) {
      var h = (host || '').toLowerCase();
      if (h.indexOf('brevo') >= 0 || h.indexOf('sendinblue') >= 0) return 'brevo';
      if (h.indexOf('gmail') >= 0) return 'gmail';
      if (h === '') return 'brevo';
      return 'custom';
    },
    onEmailProviderChange() {
      if (this.emailProvider === 'brevo') {
        this.notif.smtp_host   = 'smtp-relay.brevo.com';
        this.notif.smtp_port   = 2525;
        this.notif.smtp_secure = 'tls';
      } else if (this.emailProvider === 'gmail') {
        this.notif.smtp_host   = 'smtp.gmail.com';
        this.notif.smtp_port   = 587;
        this.notif.smtp_secure = 'tls';
      }
      // 'custom' leaves the current values editable
    },
    async loadNotificationSettings() {
      try {
        var r = await executeJson({ function: 'getNotificationSettings' }, hms);
        if (r.data && r.data.success && r.data.data && r.data.data.Settings) {
          var s = r.data.data.Settings;
          this.notif = Object.assign(this.blankNotif(), {
            sms_username:    s.sms_username    || '',
            sms_api_key:     s.sms_api_key     || '',
            sms_sender_id:   s.sms_sender_id   || '',
            sms_sandbox:     Number(s.sms_sandbox || 0),
            smtp_host:       s.smtp_host       || '',
            smtp_port:       Number(s.smtp_port || 2525),
            smtp_username:   s.smtp_username   || '',
            smtp_password:   s.smtp_password   || '',
            smtp_from_email: s.smtp_from_email || '',
            smtp_from_name:  s.smtp_from_name  || '',
            smtp_secure:     s.smtp_secure     || 'tls',
            quiet_hours_start: s.quiet_hours_start ? String(s.quiet_hours_start).slice(0,5) : '',
            quiet_hours_end:   s.quiet_hours_end   ? String(s.quiet_hours_end).slice(0,5)   : '',
            daily_cap:       Number(s.daily_cap || 0),
            is_active:       Number(s.is_active || 0)
          });
        } else {
          this.notif = this.blankNotif();
        }
        this.emailProvider = this.inferEmailProvider(this.notif.smtp_host);
      } catch(e) { console.error(e); }
    },
    async saveNotifSettings() {
      // Validate before saving — Brevo/Gmail SMTP both reject empty MAIL FROM
      var fromEmail = (this.notif.smtp_from_email || '').trim();
      if (!fromEmail || !/^\S+@\S+\.\S+$/.test(fromEmail)) {
        this.showToast('From Email is required (must be a verified Brevo sender, e.g. sstmgenerated@gmail.com)', true);
        return;
      }
      if (!(this.notif.smtp_host || '').trim() || !(this.notif.smtp_username || '').trim()) {
        this.showToast('SMTP host and username are required', true);
        return;
      }
      this.notifSaving = true;
      try {
        var r = await executeJson(Object.assign({ function: 'saveNotificationSettings' }, this.notif), hms);
        if (r.data && r.data.success) {
          this.showToast('Notification settings saved');
          await this.loadNotificationSettings();
        } else {
          this.showToast((r.data && r.data.message) || 'Failed to save', true);
        }
      } catch(e) { this.showToast('Connection error', true); }
      this.notifSaving = false;
    },
    async doSendTestSms() {
      var phone = (this.smsTest.phone || '').replace(/\s+/g, '');
      if (!/^(?:\+?254|0)(?:7|1)\d{8}$/.test(phone)) {
        this.smsTest.result = 'Enter a valid Kenyan phone (07XX, 01XX, or 2547XX).';
        this.smsTest.ok = false; return;
      }
      this.smsTest.busy = true; this.smsTest.result = '';
      try {
        var r = await executeJson({
          function: 'testNotificationSms',
          phone:    phone,
          message:  this.smsTest.message
        }, hms);
        this.smsTest.ok = !!(r.data && r.data.success);
        this.smsTest.result = (r.data && r.data.message) || (this.smsTest.ok ? 'Sent.' : 'Failed.');
      } catch(e) { this.smsTest.ok = false; this.smsTest.result = 'Connection error.'; }
      this.smsTest.busy = false;
    },
    async doSendTestEmail() {
      var email = (this.emailTest.email || '').trim();
      if (!/^\S+@\S+\.\S+$/.test(email)) {
        this.emailTest.result = 'Enter a valid email address.';
        this.emailTest.ok = false; return;
      }
      this.emailTest.busy = true; this.emailTest.result = '';
      try {
        var r = await executeJson({
          function: 'testNotificationEmail',
          email:    email,
          subject:  this.emailTest.subject
        }, hms);
        this.emailTest.ok = !!(r.data && r.data.success);
        this.emailTest.result = (r.data && r.data.message) || (this.emailTest.ok ? 'Sent.' : 'Failed.');
      } catch(e) { this.emailTest.ok = false; this.emailTest.result = 'Connection error.'; }
      this.emailTest.busy = false;
    },

    async loadPaybills() {
      try {
        var r = await executeJson({ function: 'getHospitalPaybills' }, hms);
        if (r.data && r.data.success) {
          this.paybills = (r.data.data.rows || []).map(p => Object.assign({}, p, { _testing:false, _registering:false, _stkTesting:false, _msg:'', _msgOk:false }));
        }
      } catch(e) { console.error(e); }
    },
    openPaybillForm() { this.pbForm = this.blankPaybillForm(); this.pbModal = true; },
    editPaybill(pb) {
      this.pbForm = {
        id: pb.id, shortcode: pb.shortcode, till_number: pb.till_number || '', account_prefix: pb.account_prefix || '',
        label: pb.label, purpose: pb.purpose, type: pb.type,
        consumer_key: pb.consumer_key || '',
        consumer_secret: pb.has_secret == 1 ? '••••••••' : '',
        passkey: pb.has_passkey == 1 ? '••••••••' : '',
        environment: pb.environment, initiator_name: pb.initiator_name || '',
        initiator_password: '••••••••',
        security_credential: pb.has_security_credential == 1 ? '••••••••' : '',
        is_primary: pb.is_primary
      };
      this.pbModal = true;
    },
    async savePaybill() {
      this.savingPb = true;
      try {
        var payload = Object.assign({}, this.pbForm);
        // Don't overwrite secrets if masked dots came back unchanged
        ['consumer_secret','passkey','initiator_password','security_credential'].forEach(k => {
          if (payload[k] && payload[k].indexOf('••') === 0) delete payload[k];
        });
        var fn = this.pbForm.id ? 'updateHospitalPaybill' : 'addHospitalPaybill';
        var r = await executeJson(Object.assign({ function: fn }, payload), hms);
        if (r.data && r.data.success) {
          this.pbModal = false;
          this.showToast(this.pbForm.id ? 'Paybill updated' : 'Paybill added');
          await this.loadPaybills();
        } else { this.showToast((r.data && r.data.message) || 'Save failed', true); }
      } catch(e) { this.showToast('Connection error', true); }
      this.savingPb = false;
    },
    async deletePaybill(pb) {
      if (!confirm('Delete paybill ' + pb.shortcode + (pb.account_prefix ? ' (' + pb.account_prefix + ')' : '') + '?')) return;
      var r = await executeJson({ function: 'deleteHospitalPaybill', id: pb.id }, hms);
      if (r.data && r.data.success) { this.showToast('Deleted'); await this.loadPaybills(); }
    },
    async setPrimary(pb) {
      var r = await executeJson({ function: 'setPrimaryHospitalPaybill', id: pb.id }, hms);
      if (r.data && r.data.success) { this.showToast('Primary updated'); await this.loadPaybills(); }
    },
    async testConn(pb) {
      pb._testing = true; pb._msg = '';
      try {
        var r = await executeJson({ function: 'testHospitalPaybillConnection', id: pb.id }, hms);
        var msg = (r.data && r.data.message) || (r.data && r.data.success ? 'Connected' : 'Failed');
        var ok = !!(r.data && r.data.success);
        // Refresh list but preserve our message on the matching row
        if (ok) {
          await this.loadPaybills();
          var replacement = this.paybills.find(x => x.id === pb.id);
          if (replacement) { replacement._msg = msg; replacement._msgOk = ok; }
        } else {
          pb._msg = msg; pb._msgOk = ok;
        }
      } catch(e) { pb._msg = 'Connection error'; pb._msgOk = false; }
      pb._testing = false;
    },
    async registerCb(pb) {
      if (!confirm('Register C2B confirmation + validation URLs with Safaricom for ' + pb.shortcode + '?')) return;
      pb._registering = true; pb._msg = '';
      try {
        var r = await executeJson({ function: 'registerHospitalPaybillCallbacks', id: pb.id }, hms);
        var msg = (r.data && r.data.message) || 'Done';
        var ok = !!(r.data && r.data.success);
        if (ok) {
          await this.loadPaybills();
          var rep = this.paybills.find(x => x.id === pb.id);
          if (rep) { rep._msg = msg; rep._msgOk = ok; }
        } else { pb._msg = msg; pb._msgOk = ok; }
      } catch(e) { pb._msg = 'Connection error'; pb._msgOk = false; }
      pb._registering = false;
    },
    testStk(pb) {
      this.stkForm = { paybill_id: pb.id, paybill_label: pb.shortcode + ' · ' + pb.label, phone: '', amount: 1, description: 'HMS test STK' };
      this.stkStatus = ''; this.stkOk = null; this.stkCheckoutId = '';
      this.stkModal = true;
    },
    closeStkModal() { if (this.stkTesting) return; this.stkModal = false; },
    resetStkForm() { this.stkStatus = ''; this.stkOk = null; this.stkCheckoutId = ''; this.stkForm.phone = ''; },
    async sendStkTest() {
      if (!this.canSendStk) return;
      this.stkTesting = true; this.stkStatus = '⏳ Sending STK push to phone…'; this.stkOk = null;
      try {
        var r = await executeJson({
          function: 'testHospitalPaybillStk',
          id: this.stkForm.paybill_id,
          phone: this.stkForm.phone,
          amount: this.stkForm.amount,
          description: this.stkForm.description
        }, hms);
        if (!r.data || !r.data.success) {
          this.stkStatus = '❌ ' + ((r.data && r.data.message) || 'STK push failed.');
          this.stkOk = false; this.stkTesting = false; return;
        }
        var checkoutId = r.data.data && r.data.data.checkout_request_id;
        if (!checkoutId) {
          this.stkStatus = '✓ STK sent (no checkout id returned — cannot track status).';
          this.stkOk = true; this.stkTesting = false; return;
        }
        this.stkCheckoutId = checkoutId;
        this.stkStatus = '📲 STK push sent. Look at your phone — you have ~90 seconds to enter your PIN.';
        await this.pollStkModal(checkoutId);
      } catch(e) { this.stkStatus = '❌ Connection error'; this.stkOk = false; }
      this.stkTesting = false;
    },
    async pollStkModal(checkoutId) {
      for (var i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          var r = await executeJson({ function: 'getStkPushStatus', checkout_request_id: checkoutId }, hms);
          var d = r.data && r.data.data;
          if (!d) continue;
          if (d.status === 'success') {
            this.stkStatus = '✅ Payment received! Receipt ' + (d.mpesa_receipt || '—') + ' · KES ' + d.amount;
            this.stkOk = true; return;
          }
          if (d.status === 'cancelled') {
            this.stkStatus = '🚫 Cancelled by user (pressed Cancel on phone). Code ' + (d.response_code || '1032');
            this.stkOk = false; return;
          }
          if (d.status === 'failed') {
            this.stkStatus = '❌ ' + (d.human_message || d.response_description || 'STK failed') + (d.response_code ? ' (code ' + d.response_code + ')' : '');
            this.stkOk = false; return;
          }
          if (d.status === 'timeout') {
            this.stkStatus = '⌛ Timed out — user did not respond on phone.';
            this.stkOk = false; return;
          }
          this.stkStatus = '📲 ' + (d.human_message || 'Waiting for PIN…') + '  (' + (d.seconds_since_initiated || 0) + 's)';
        } catch(e) {}
      }
      // No callback after 90s — actively query Safaricom
      this.stkStatus = '⏳ No callback after 90s. Querying Safaricom directly…';
      try {
        var q = await executeJson({ function: 'queryStkPushFromSafaricom', checkout_request_id: checkoutId }, hms);
        var r2 = await executeJson({ function: 'getStkPushStatus', checkout_request_id: checkoutId }, hms);
        var d2 = r2.data && r2.data.data;
        if (d2 && d2.status === 'success') { this.stkStatus = '✅ Payment received (via direct query). Receipt ' + d2.mpesa_receipt; this.stkOk = true; }
        else if (d2) { this.stkStatus = '🔍 ' + (d2.human_message || d2.response_description || d2.status); this.stkOk = d2.status === 'success'; }
        else { this.stkStatus = '⚠ Could not determine final status.'; this.stkOk = false; }
      } catch(e) { this.stkStatus = '⚠ Timed out and direct query failed.'; this.stkOk = false; }
    },
    async pollStkStatus(pb, checkoutId) {
      var maxTries = 30;       // 30 × 3s = 90s
      for (var i = 0; i < maxTries; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          var r = await executeJson({ function: 'getStkPushStatus', checkout_request_id: checkoutId }, hms);
          if (r.data && r.data.success && r.data.data) {
            var d = r.data.data;
            if (d.status === 'success') {
              pb._msg = '✅ Payment received! Receipt: ' + (d.mpesa_receipt || '—') + ' · KES ' + d.amount;
              pb._msgOk = true; return;
            }
            if (d.status === 'cancelled') {
              pb._msg = '🚫 Cancelled by user (pressed Cancel on phone). Code ' + (d.response_code || '1032');
              pb._msgOk = false; return;
            }
            if (d.status === 'failed') {
              pb._msg = '❌ ' + (d.human_message || d.response_description || 'STK failed') + (d.response_code ? ' (code ' + d.response_code + ')' : '');
              pb._msgOk = false; return;
            }
            if (d.status === 'timeout') {
              pb._msg = '⌛ Timed out — user did not respond on phone.';
              pb._msgOk = false; return;
            }
            // still pending — show elapsed time
            pb._msg = '📲 ' + (d.human_message || 'Waiting…') + ' (' + (d.seconds_since_initiated || 0) + 's elapsed)';
            pb._msgOk = true;
          }
        } catch(e) { /* swallow and keep polling */ }
      }
      // 90s elapsed without callback — try active Safaricom query
      pb._msg = '⏳ No callback after 90s. Querying Safaricom directly…';
      try {
        var q = await executeJson({ function: 'queryStkPushFromSafaricom', checkout_request_id: checkoutId }, hms);
        if (q.data && q.data.success) {
          // Re-fetch our DB row which the active query should have updated
          var r2 = await executeJson({ function: 'getStkPushStatus', checkout_request_id: checkoutId }, hms);
          var d2 = r2.data && r2.data.data;
          if (d2) {
            pb._msg = '🔍 Safaricom says: ' + (d2.human_message || d2.response_description || d2.status);
            pb._msgOk = d2.status === 'success';
          } else {
            pb._msg = '🔍 Safaricom: ' + ((q.data && q.data.message) || 'unknown');
            pb._msgOk = false;
          }
        } else {
          pb._msg = '⚠ Timed out and Safaricom query failed: ' + ((q.data && q.data.message) || 'unknown');
          pb._msgOk = false;
        }
      } catch(e) { pb._msg = '⚠ Timed out and direct query failed.'; pb._msgOk = false; }
    }
  }
};
</script>
